import { useState, useCallback } from "react";
import { toast } from "sonner";
import {
  createSmartfileSession,
  getSmartfileSession,
  generateSmartfileInsights,
  extractPatientInfoFromText,
  type SmartfileSession,
} from "@/services/smartfile";
import { processFileUploadUltraFast, processTextInputUltraFast } from "@/services/smartfile-fast";
import { supabase } from "@/integrations/supabase/client";

export interface UseOnboardingUploadOptions {
  onSessionCreated: (sessionId: string, session: SmartfileSession) => void;
  onPatientLinked?: (patient: { id: string; name: string; cpf?: string }) => void;
}

export function useOnboardingUpload({
  onSessionCreated,
  onPatientLinked,
}: UseOnboardingUploadOptions) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState("");

  const linkPatientFromDocument = useCallback(
    async (sessionId: string, extractedText: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const patientInfo = await extractPatientInfoFromText(extractedText);
      if (!patientInfo.name && !patientInfo.cpf) return;

      let query = supabase
        .from("patients_onboarding")
        .select("*")
        .eq("owner_id", user.id);

      if (patientInfo.cpf) {
        const cleanCpf = patientInfo.cpf.replace(/\D/g, "");
        query = query.eq("cpf", cleanCpf);
      } else if (patientInfo.name) {
        query = query.ilike("full_name", `%${patientInfo.name}%`);
      }

      const { data: existingPatients, error: searchError } = await query.limit(1);
      if (searchError) return;

      if (existingPatients && existingPatients.length > 0) {
        // Paciente existente: vincular à sessão
        const p = existingPatients[0];
        await supabase
          .from("smartfile_sessions")
          .update({ patient_id: p.id })
          .eq("id", sessionId);

        onPatientLinked?.({ id: p.id, name: p.full_name, cpf: p.cpf });
        toast.success(`✅ Paciente "${p.full_name}" vinculado automaticamente!`);
      } else {
        // NÃO criar paciente aqui - apenas salvar dados extraídos para preencher os inputs no Step 4
        // O paciente será criado SOMENTE ao finalizar o Step 4
        const tempData = {
          name: patientInfo.name || "",
          cpf: patientInfo.cpf ? patientInfo.cpf.replace(/\D/g, "") : "",
        };
        await supabase
          .from("smartfile_sessions")
          .update({ temp_patient_data: tempData })
          .eq("id", sessionId);

        toast.success(`✅ Nome e CPF identificados no documento. Confira e finalize no próximo passo.`);
      }
    },
    [onPatientLinked]
  );

  const processFile = useCallback(
    async (file: File) => {
      setUploading(true);
      setUploadProgress(0);
      setUploadStage("Iniciando...");

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Usuário não autenticado");

        const newSessionId = await createSmartfileSession(null);
        await processFileUploadUltraFast(newSessionId, file, (stage, progress) => {
          setUploadStage(stage);
          setUploadProgress(progress);
        });

        let session = await getSmartfileSession(newSessionId);
        if (session.content_type === "other") {
          await deleteInvalidSession(newSessionId, session.file_path);
          toast.error(
            "O arquivo enviado não possui dados clínicos de saúde. Favor verificar."
          );
          return null;
        }

        if (session.extracted_text) {
          await linkPatientFromDocument(newSessionId, session.extracted_text);
          session = await getSmartfileSession(newSessionId);
        }

        if (session.structured_records?.length) {
          await generateSmartfileInsights(
            newSessionId,
            session.structured_records,
            session.temp_patient_data || {}
          );
          session = await getSmartfileSession(newSessionId);
        }

        onSessionCreated(newSessionId, session);
        toast.success("✅ Arquivo processado com sucesso!");
        return newSessionId;
      } catch (e: unknown) {
        const err = e instanceof Error ? e.message : String(e);
        toast.error(err || "Erro ao processar arquivo");
        return null;
      } finally {
        setUploading(false);
        setUploadProgress(0);
        setUploadStage("");
      }
    },
    [linkPatientFromDocument, onSessionCreated]
  );

  const processText = useCallback(
    async (text: string) => {
      setUploading(true);
      setUploadProgress(0);
      setUploadStage("Criando sessão...");

      try {
        const newSessionId = await createSmartfileSession(null);
        setUploadProgress(10);

        await processTextInputUltraFast(newSessionId, text, (stage, progress) => {
          setUploadStage(stage);
          setUploadProgress(progress);
        });

        let session = await getSmartfileSession(newSessionId);
        if (session.content_type === "other") {
          await deleteInvalidSession(newSessionId, session.file_path);
          toast.error(
            "O texto inserido não possui dados clínicos de saúde. Favor verificar."
          );
          return null;
        }

        if (session.extracted_text) {
          await linkPatientFromDocument(newSessionId, session.extracted_text);
          session = await getSmartfileSession(newSessionId);
        }

        if (session.structured_records?.length) {
          await generateSmartfileInsights(
            newSessionId,
            session.structured_records,
            session.temp_patient_data || {}
          );
          session = await getSmartfileSession(newSessionId);
        }

        onSessionCreated(newSessionId, session);
        toast.success("✅ Texto processado com sucesso!");
        return newSessionId;
      } catch (e: unknown) {
        const err = e instanceof Error ? e.message : String(e);
        toast.error(err || "Erro ao processar texto");
        return null;
      } finally {
        setUploading(false);
        setUploadProgress(0);
        setUploadStage("");
      }
    },
    [linkPatientFromDocument, onSessionCreated]
  );

  return {
    uploading,
    uploadProgress,
    uploadStage,
    processFile,
    processText,
  };
}

async function deleteInvalidSession(
  sessionId: string,
  filePath?: string | null
) {
  if (filePath) {
    try {
      await supabase.storage.from("patient-docs").remove([filePath]);
    } catch {
      /* ignore */
    }
  }
  try {
    await supabase.from("lab_results_onboarding").delete().eq("source_session_id", sessionId);
  } catch {
    /* ignore */
  }
  await supabase.from("smartfile_sessions").delete().eq("id", sessionId);
}

